import React, { useState, useEffect } from 'react';
import { FiPlus, FiMinus, FiUsers, FiUser } from 'react-icons/fi';
import { BsBuilding } from 'react-icons/bs';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

function AuthorFields({
  authors,
  setAuthors,
  authorType,
  setAuthorType,
  showAuthorType = true,
  label = "Autores",
  showGender = false
}) {
  const [showPersonalFields, setShowPersonalFields] = useState(true);
  const [showInstitutionalFields, setShowInstitutionalFields] = useState(false);

  // Efecto para establecer los tipos de autor disponibles según el estado inicial
  useEffect(() => {
    if (authorType === 'both') {
      setShowPersonalFields(true);
      setShowInstitutionalFields(true);
    } else if (authorType === 'personal') {
      setShowPersonalFields(true);
      setShowInstitutionalFields(false);
    } else if (authorType === 'institutional') {
      setShowPersonalFields(false);
      setShowInstitutionalFields(true);
    }
  }, [authorType]);

  const addAuthor = (type) => {
    if (type === 'personal') {
      setAuthors([
        ...authors,
        {
          type: 'personal',
          lastNames: '',
          firstNames: '',
          gender: showGender ? 'masculine' : undefined
        }
      ]);
    } else {
      setAuthors([
        ...authors,
        {
          type: 'institutional',
          name: '',
          abbreviation: ''
        }
      ]);
    }
  };

  const removeAuthor = (index) => {
    setAuthors(authors.filter((_, i) => i !== index));
  };

  const updateAuthor = (index, field, value) => {
    const updatedAuthors = authors.map((author, i) =>
      i === index ? { ...author, [field]: value } : author
    );
    setAuthors(updatedAuthors);
  };

  const handleAuthorTypeChange = () => {
    // Determinar el nuevo tipo de autor basado en las casillas seleccionadas
    if (showPersonalFields && showInstitutionalFields) {
      setAuthorType('both');
    } else if (showPersonalFields) {
      setAuthorType('personal');
    } else if (showInstitutionalFields) {
      setAuthorType('institutional');
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2
      }
    }
  };

  const buttonVariants = {
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  };

  return (
    <motion.div
      className="space-y-3 sm:space-y-4"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {showAuthorType && (
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
            Tipo de {label.toLowerCase()}
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showPersonalFields}
                onChange={() => {
                  // No permitir desmarcar ambas casillas
                  if (showInstitutionalFields || !showPersonalFields) {
                    setShowPersonalFields(!showPersonalFields);
                    setTimeout(handleAuthorTypeChange, 0);
                  }
                }}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="flex items-center dark:text-gray-300">
                <SafeIcon icon={FiUser} className="w-4 h-4 mr-1 text-blue-600 dark:text-blue-400" />
                Personal
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showInstitutionalFields}
                onChange={() => {
                  // No permitir desmarcar ambas casillas
                  if (showPersonalFields || !showInstitutionalFields) {
                    setShowInstitutionalFields(!showInstitutionalFields);
                    setTimeout(handleAuthorTypeChange, 0);
                  }
                }}
                className="mr-2 h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="flex items-center dark:text-gray-300">
                <SafeIcon icon={BsBuilding} className="w-4 h-4 mr-1 text-pink-600 dark:text-pink-400" />
                Institucional
              </span>
            </label>
          </div>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-3 sm:p-4 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700 flex items-center dark:text-gray-300">
            <SafeIcon icon={FiUsers} className="w-4 h-4 mr-1.5 text-gray-600 dark:text-gray-400" />
            {label}
          </label>
          <div className="flex flex-wrap gap-2">
            {showPersonalFields && (
              <motion.button
                type="button"
                onClick={() => addAuthor('personal')}
                className="flex items-center space-x-1 px-2 py-1 text-xs sm:text-sm bg-[#2B5596] text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <SafeIcon icon={FiPlus} className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">Agregar Personal</span>
                <span className="inline xs:hidden">Personal</span>
              </motion.button>
            )}
            {showInstitutionalFields && (
              <motion.button
                type="button"
                onClick={() => addAuthor('institutional')}
                className="flex items-center space-x-1 px-2 py-1 text-xs sm:text-sm bg-[#F54CC8] text-white rounded-md hover:bg-pink-700 transition-colors shadow-sm"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <SafeIcon icon={FiPlus} className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">Agregar Institucional</span>
                <span className="inline xs:hidden">Institucional</span>
              </motion.button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {authors.map((author, index) => (
            <motion.div
              key={index}
              className="flex flex-col xs:flex-row xs:items-end space-y-2 xs:space-y-0 xs:space-x-2 mb-4"
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              layout
            >
              {author.type === 'personal' ? (
                <>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1 dark:text-gray-400">
                      Apellidos completos *
                    </label>
                    <input
                      type="text"
                      value={author.lastNames || ''}
                      onChange={(e) => updateAuthor(index, 'lastNames', e.target.value)}
                      className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1 dark:text-gray-400">
                      Nombres completos *
                    </label>
                    <input
                      type="text"
                      value={author.firstNames || ''}
                      onChange={(e) => updateAuthor(index, 'firstNames', e.target.value)}
                      className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                      required
                    />
                  </div>
                  {showGender && (
                    <div className="w-full xs:w-32">
                      <label className="block text-xs text-gray-600 mb-1 dark:text-gray-400">
                        Género
                      </label>
                      <select
                        value={author.gender || 'masculine'}
                        onChange={(e) => updateAuthor(index, 'gender', e.target.value)}
                        className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                      >
                        <option value="masculine">Masculino</option>
                        <option value="feminine">Femenino</option>
                      </select>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1 dark:text-gray-400">
                      Nombre de la institución *
                    </label>
                    <input
                      type="text"
                      value={author.name || ''}
                      onChange={(e) => updateAuthor(index, 'name', e.target.value)}
                      className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                      required
                    />
                  </div>
                  <div className="w-full xs:w-32">
                    <label className="block text-xs text-gray-600 mb-1 dark:text-gray-400">
                      Sigla/Abreviatura
                    </label>
                    <input
                      type="text"
                      value={author.abbreviation || ''}
                      onChange={(e) => updateAuthor(index, 'abbreviation', e.target.value)}
                      className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                    />
                  </div>
                </>
              )}
              <motion.button
                type="button"
                onClick={() => removeAuthor(index)}
                className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors dark:hover:bg-red-900/30 self-end"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <SafeIcon icon={FiMinus} className="w-3 h-3 sm:w-4 sm:h-4" />
              </motion.button>
            </motion.div>
          ))}
        </AnimatePresence>

        {authors.length === 0 && (
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            {showPersonalFields && (
              <motion.button
                type="button"
                onClick={() => addAuthor('personal')}
                className="flex-1 py-4 sm:py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors dark:border-gray-600 dark:text-gray-400 dark:hover:border-blue-500 dark:hover:text-blue-400"
                whileHover={{
                  scale: 1.01,
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)"
                }}
              >
                <div className="flex flex-col items-center">
                  <SafeIcon icon={FiUser} className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2" />
                  <span className="text-xs sm:text-sm text-center">
                    Agregar {label.toLowerCase()} personal
                  </span>
                </div>
              </motion.button>
            )}
            {showInstitutionalFields && (
              <motion.button
                type="button"
                onClick={() => addAuthor('institutional')}
                className="flex-1 py-4 sm:py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-pink-400 hover:text-pink-600 transition-colors dark:border-gray-600 dark:text-gray-400 dark:hover:border-pink-500 dark:hover:text-pink-400"
                whileHover={{
                  scale: 1.01,
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)"
                }}
              >
                <div className="flex flex-col items-center">
                  <SafeIcon icon={BsBuilding} className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2" />
                  <span className="text-xs sm:text-sm text-center">
                    Agregar {label.toLowerCase()} institucional
                  </span>
                </div>
              </motion.button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default AuthorFields;